<template>
    <div v-if="open" class="cv3-modal-overlay" @click.self="close">
        <div class="cv3-vmodal">
            <header class="cv3-vmodal-head">
                <div class="cv3-vmodal-title !ppv2-text-white">
                    <GiftIcon :size="18" />
                    <h2 class="!ppv2-text-white">{{ validateLabel }}</h2>
                </div>
                <button type="button" class="cv3-modal-close" @click="close"><XIcon :size="20" /></button>
            </header>

            <div class="cv3-vmodal-body">
                <!-- Destinataire -->
                <BaseRadioGroup
                    v-model="form.recipientMode"
                    :label="$t('checkout.v3.recipient_label')"
                    :options="[
                        { label: $t('checkout.v3.recipient_none'), value: 'none' },
                        { label: $t('checkout.v3.recipient_other'), value: 'other' },
                    ]"
                    class="ppv2-mb-2 colorWhiteInside"
                />

                <!-- Sans destinataire : rien à remplir, on valide directement. -->
                <p v-if="form.recipientMode === 'none'" class="cv3-vhint">
                    {{ $t('checkout.v3.no_info_required_hint') }}
                </p>

                <!-- Autre personne : on saisit le destinataire + l'expéditeur + l'envoi. -->
                <template v-if="form.recipientMode === 'other'">
                    <div class="cv3-vgrid">
                        <input v-model="form.to_first_name" :placeholder="$t('checkout.v3.recipient_first_name_placeholder')" />
                        <input v-model="form.to_last_name" :placeholder="$t('checkout.v3.recipient_last_name_placeholder')" />
                        <input
                            v-model="form.to_email"
                            type="email"
                            :placeholder="$t('checkout.v3.recipient_email_placeholder')"
                        />
                        <input v-model="form.to_phone" :placeholder="$t('checkout.v3.phone_placeholder')" />
                    </div>

                    <!-- Offert par -->
                    <p class="cv3-vlabel">{{ $t('checkout.v3.given_by_label') }}</p>
                    <div class="cv3-vgrid">
                        <input v-model="form.from_first_name" :placeholder="$t('checkout.v3.first_name_placeholder')" />
                        <input v-model="form.from_last_name" :placeholder="$t('checkout.v3.last_name_placeholder')" />
                        <input v-model="form.from_email" type="email" :placeholder="$t('checkout.v3.email_placeholder')" />
                        <input v-model="form.from_phone_number" :placeholder="$t('checkout.v3.phone_placeholder')" />
                    </div>

                    <!-- Envoi -->
                    <p class="cv3-vlabel">{{ $t('checkout.v3.sending_label') }}</p>
                    <div class="cv3-vgrid">
                        <input v-model="form.send_date" type="date" />
                        <BaseCheckbox v-model="form.no_mail" :label="$t('checkout.v3.no_mail_label')" has-border-checked />
                    </div>
                    <BaseTextarea
                        v-model="form.from_message"
                        :rows="2"
                        :placeholder="$t('checkout.v3.message_placeholder')"
                        :use-editor="false"
                    />
                </template>
            </div>

            <footer class="cv3-vmodal-foot">
                <button type="button" class="cv3-acompte-btn" @click="close">{{ $t('checkout.v3.cancel_btn') }}</button>
                <button type="button" class="cv3-validate-pay" :disabled="store.busy || !isValid" @click="validate">
                    <CheckIcon :size="18" /> {{ validateLabel }}
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { Gift as GiftIcon, X as XIcon, Check as CheckIcon } from 'lucide-vue'
import BaseRadioGroup from '../../../Shared/BaseRadioGroup.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'

export default {
    name: 'VoucherValidationModal',
    components: { GiftIcon, XIcon, CheckIcon, BaseRadioGroup, BaseTextarea },
    props: {
        store: { type: Object, required: true },
        open: { type: Boolean, default: false },
    },
    data() {
        return { form: this.blank() }
    },
    computed: {
        isValid() {
            // No recipient → nothing to fill in, validate directly (legacy isFormValid: 'none' => true).
            if (this.form.recipientMode === 'none') return true
            // Another person → their email is required (the voucher is sent to them).
            if (this.form.recipientMode === 'other' && !this.form.to_email) return false
            return true
        },
        // "Valider le billet non daté" when the cart holds undated-ticket drafts, else "Valider le bon cadeau".
        validateLabel() {
            const undated = (this.store.drafts || []).some((d) => d.isUndatedTicket)
            return this.$t(undated ? 'checkout.voucher.validate_undated' : 'checkout.voucher.validate_gift')
        },
    },
    watch: {
        open(v) {
            if (v) this.form = this.blank()
        },
    },
    methods: {
        blank() {
            const today = new Date()
            const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
                today.getDate()
            ).padStart(2, '0')}`
            // Prefill "offert par" from the current customer when known.
            const c = this.store.currentCustomer || {}
            return {
                recipientMode: 'none',
                to_first_name: '',
                to_last_name: '',
                to_email: '',
                to_phone: '',
                from_first_name: c.first_name || '',
                from_last_name: c.last_name || '',
                from_email: c.email || '',
                from_phone_number: c.phone_number || '',
                from_message: '',
                send_date: iso,
                no_mail: false,
            }
        },
        close() {
            this.$emit('close')
        },
        async validate() {
            if (!this.isValid || this.store.busy) return
            const f = this.form
            const payload = {
                send_date: f.send_date,
                from_first_name: f.from_first_name,
                from_last_name: f.from_last_name,
                from_email: f.from_email,
                from_phone_number: f.from_phone_number,
                from_message: f.from_message,
                no_mail: f.no_mail ? 'on' : '',
                isUndatedTicket: 'false',
            }
            if (f.recipientMode === 'none') {
                payload.no_receiver = 'on'
            } else {
                payload.new_customer = 'on'
                payload.to_first_name = f.to_first_name
                payload.to_last_name = f.to_last_name
                payload.to_email = f.to_email
                payload.to_phone = f.to_phone
            }
            try {
                await this.store.validateVoucher(payload)
                this.$toast && this.$toast.success(this.$t('checkout.v3.voucher_validated'))
                this.$emit('validated')
                this.close()
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_validating_voucher'))
            }
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1100;
    background: rgba(3, 6, 20, 0.7);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.cv3-vmodal {
    width: 100%;
    max-width: 560px;
    max-height: 92vh;
    background: var(--cv3-panel);
    border: 1px solid var(--cv3-border-input);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
    color: var(--cv3-text);
}
.cv3-vmodal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 22px;
    border-bottom: 1px solid var(--cv3-border);
}
.cv3-vmodal-title {
    display: flex;
    align-items: center;
    gap: 10px;
    h2 {
        margin: 0;
        font-size: 17px;
        font-weight: 700;
    }
}
.cv3-modal-close {
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    display: inline-flex;
}
.cv3-vmodal-body {
    padding: 18px 22px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-height: 0;
}
.cv3-vlabel {
    margin: 6px 0 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--cv3-muted);
}
.cv3-vhint {
    margin: 4px 0 0;
    font-size: 13px;
    color: var(--cv3-text-2);
}
.cv3-vradios {
    display: flex;
    gap: 10px;
    label {
        flex: 1 1 0;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        border-radius: 10px;
        border: 1px solid var(--cv3-border-input);
        font-size: 13px;
        cursor: pointer;
        &.active {
            border-color: var(--cv3-accent);
            background: var(--cv3-icon-tint);
        }
    }
}
.cv3-vgrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}
.cv3-vmodal-body input[type='text'],
.cv3-vmodal-body input:not([type]),
.cv3-vmodal-body input[type='email'],
.cv3-vmodal-body input[type='date'],
.cv3-vtextarea {
    background: var(--cv3-input);
    border: 1px solid var(--cv3-border-input);
    border-radius: 9px;
    color: var(--cv3-text);
    padding: 9px 11px;
    font-size: 13px;
    width: 100%;
    &::placeholder {
        color: var(--cv3-muted);
    }
}
.cv3-vcheck {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--cv3-text-2);
}
.cv3-vmodal-foot {
    padding: 16px 22px;
    border-top: 1px solid var(--cv3-border);
    display: flex;
    gap: 10px;
}
.cv3-acompte-btn {
    flex: 0 0 auto;
    padding: 15px 20px;
    border-radius: 13px;
    border: 1px solid var(--cv3-border-input);
    background: transparent;
    color: var(--cv3-text-2);
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
}
.cv3-validate-pay {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px;
    border-radius: 13px;
    border: 0;
    background: var(--cv3-accent);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    box-shadow: 0 9px 23px -7px rgba(125, 45, 189, 0.7);
    cursor: pointer;
    &:disabled {
        box-shadow: none;
        opacity: 0.6;
        cursor: not-allowed;
    }
}
</style>
